import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc,
  getDoc,
  updateDoc,
  getDocs,
  collection,
  query,
  where,
  arrayUnion,
  arrayRemove,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useUser } from '../../context/UserContext';
import Swal from 'sweetalert2';
import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import PhotoUpload from '../../components/PhotoUpload';

// ✅ Interfaz de dirección
interface Direccion {
  calle: string;
  numero: string;
  unidad: string;
  area: string;
  zona: string;
  estado: string;
  codigo_postal: string;
}

// ✅ Interfaz de miembro (asesor o asociado)
interface Miembro {
  id: string;
  nombre: string;
  tipo: 'Afiliado' | 'Asociado';
}

export default function EditarPropiedad() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();

  const [propiedadOriginal, setPropiedadOriginal] = useState<any>(null); // Guardar datos originales
  const [inmobiliarias, setInmobiliarias] = useState<any[]>([]);
  const [asesoresDisponibles, setAsesoresDisponibles] = useState<Miembro[]>([]);
  const [proyectos, setProyectos] = useState<any[]>([]);
  const [tasaCambio, setTasaCambio] = useState(20); // Tasa de cambio USD -> MXN

  const [formData, setFormData] = useState({
    // IDs
    propiedad_id: '',
    list_number: '', // ID FlexMLS

    // Básico
    titulo: '',
    descripcion: '',
    tipo: '',
    estado: '',

    // Ubicación (OBJETO)
    direccion: {
      calle: '',
      numero: '',
      unidad: '',
      area: '',
      zona: 'Riviera Nayarit',
      estado: 'Nayarit',
      codigo_postal: '',
    } as Direccion,

    // Especificaciones (SEPARADOS)
    recamaras: '',
    banos_completos: '',
    medios_banos: '',
    estacionamiento: '',
    m2_total: '',
    m2_lote: '',
    anos_construccion: '',
    pisos: '',

    // Precios (DOS CAMPOS)
    precio_actual_usd: '',
    precio_actual_mxn: '',

    // Características
    amenidades: [] as string[],
    amueblado: false,
    mascotas: false,

    // Multimedia
    fotos: [] as string[],
    video_url: '',
    tour_virtual_url: '',

    // Relaciones (CORREGIDAS)
    inmobiliaria_doc_id: '', // STRING - Una sola agencia
    asesor_doc_ids: [] as string[], // ARRAY - Puede tener varios

    // Proyecto (opcional)
    proyecto_id: '',

    // Estado
    activo: true,
    aprobado: false,
    destacada: false,
    estatus_publicacion: 'pendiente',
  });

  // ✅ Cargar propiedad existente
  useEffect(() => {
    const fetchPropiedad = async () => {
      if (!id) return;

      const propSnap = await getDoc(doc(db, 'propiedades', id));

      if (!propSnap.exists()) {
        Swal.fire({
          icon: 'error',
          title: 'No encontrada',
          text: 'La propiedad no existe.'
        });
        return navigate('/admin-property');
      }

      const data = propSnap.data();
      setPropiedadOriginal(data); // Guardar original para comparar cambios

      // Precarga del formulario con datos existentes
      setFormData({
        propiedad_id: data.propiedad_id || '',
        list_number: data.list_number || '',
        titulo: data.titulo || data.nombre || '', // Compatibilidad con nombre viejo
        descripcion: data.descripcion || '',
        tipo: data.tipo || '',
        estado: data.estado || 'Venta',

        direccion: typeof data.direccion === 'object' ? data.direccion : {
          calle: data.direccion || '',
          numero: '',
          unidad: '',
          area: '',
          zona: 'Riviera Nayarit',
          estado: 'Nayarit',
          codigo_postal: '',
        },

        recamaras: data.recamaras?.toString() || '',
        banos_completos: (data.banos_completos || data.baños || 0).toString(),
        medios_banos: (data.medios_banos || 0).toString(),
        estacionamiento: data.estacionamiento?.toString() || '',
        m2_total: (data.m2_total || data.construccion_m2 || '').toString(),
        m2_lote: (data.m2_lote || data.terreno_m2 || '').toString(),
        anos_construccion: data.anos_construccion?.toString() || '',
        pisos: data.pisos?.toString() || '',

        precio_actual_usd: (data.precio_actual_usd || data.precio || '').toString(),
        precio_actual_mxn: (data.precio_actual_mxn || '').toString(),

        amenidades: Array.isArray(data.amenidades) ? data.amenidades : [],
        amueblado: data.amueblado || false,
        mascotas: data.mascotas || false,

        fotos: Array.isArray(data.fotos) ? data.fotos : [],
        video_url: data.video_url || '',
        tour_virtual_url: data.tour_virtual_url || '',

        // ✅ CORREGIDO: Usar campos nuevos con doc.id
        inmobiliaria_doc_id: data.inmobiliaria_doc_id || '',

        asesor_doc_ids: Array.isArray(data.asesor_doc_ids)
          ? data.asesor_doc_ids
          : [],

        proyecto_id: data.proyecto_id || '',
        activo: data.activo !== undefined ? data.activo : true,
        aprobado: data.aprobado || false,
        destacada: data.destacada || false,
        estatus_publicacion: data.estatus_publicacion || 'pendiente',
      });
    };

    fetchPropiedad();
  }, [id, navigate]);

  // ✅ Cargar inmobiliarias
  useEffect(() => {
    const fetchInmobiliarias = async () => {
      const q = query(
        collection(db, 'inmobiliaria'),
        where('activo', '==', true)
      );
      const snap = await getDocs(q);
      const datos = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setInmobiliarias(datos);
    };
    fetchInmobiliarias();
  }, []);

  // ✅ Cargar proyectos
  useEffect(() => {
    const fetchProyectos = async () => {
      const snap = await getDocs(collection(db, 'proyectos'));
      const datos = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProyectos(datos);
    };
    fetchProyectos();
  }, []);

  // ✅ Cargar asesores cuando se selecciona una agencia
  useEffect(() => {
    const fetchAsesores = async () => {
      if (!formData.inmobiliaria_doc_id) {
        setAsesoresDisponibles([]);
        return;
      }

      try {
        // Cargar afiliados (asesor)
        const qAsesores = query(
          collection(db, 'asesor'),
          where('inmobiliaria_doc_ids', 'array-contains', formData.inmobiliaria_doc_id), // ✅ Nuevo campo
          where('activo', '==', true)
        );
        const snapAsesores = await getDocs(qAsesores);
        const asesores = snapAsesores.docs.map(doc => {
          return {
            id: doc.id, // ✅ doc.id de Firestore
            nombre: `${doc.data().nombre} ${doc.data().apellido || ''} (Afiliado)`,
            tipo: 'Afiliado' as const
          };
        });

        // Cargar asociados
        const qAsociados = query(
          collection(db, 'asociado'),
          where('inmobiliaria_doc_id', '==', formData.inmobiliaria_doc_id), // ✅ Nuevo campo
          where('activo', '==', true)
        );
        const snapAsociados = await getDocs(qAsociados);
        const asociados = snapAsociados.docs.map(doc => {
          return {
            id: doc.id, // ✅ doc.id de Firestore
            nombre: `${doc.data().nombre} ${doc.data().apellido || ''} (Asociado)`,
            tipo: 'Asociado' as const
          };
        });

        // Combinar y ordenar
        const todos = [...asesores, ...asociados].sort((a, b) =>
          a.nombre.localeCompare(b.nombre)
        );

        setAsesoresDisponibles(todos);
      } catch (error) {
        console.error('Error cargando asesores:', error);
        setAsesoresDisponibles([]);
      }
    };

    fetchAsesores();
  }, [formData.inmobiliaria_doc_id]);

  // ✅ Manejar cambios en campos simples
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // ✅ Manejar cambios en dirección
  const handleDireccionChange = (field: keyof Direccion, value: string) => {
    setFormData(prev => ({
      ...prev,
      direccion: {
        ...prev.direccion,
        [field]: value
      }
    }));
  };

  // ✅ Auto-calcular precio MXN cuando cambia USD
  const handlePrecioUSDChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      precio_actual_usd: value,
      precio_actual_mxn: value ? (parseFloat(value) * tasaCambio).toFixed(2) : ''
    }));
  };

  // ✅ Manejar fotos
  const handlePhotosUpload = (urls: string[]) => {
    setFormData(prev => ({
      ...prev,
      fotos: [...prev.fotos, ...urls]
    }));
  };

  const handlePhotoDelete = (url: string) => {
    setFormData(prev => ({
      ...prev,
      fotos: prev.fotos.filter(foto => foto !== url)
    }));
  };

  // ✅ Manejar amenidades
  const toggleAmenidad = (item: string) => {
    setFormData(prev => ({
      ...prev,
      amenidades: prev.amenidades.includes(item)
        ? prev.amenidades.filter(a => a !== item)
        : [...prev.amenidades, item]
    }));
  };

  // ✅ Manejar cambio de agencia
  const handleAgenciaChange = (nuevaAgenciaId: string) => {
    setFormData(prev => ({
      ...prev,
      inmobiliaria_doc_id: nuevaAgenciaId,
      asesor_doc_ids: [] // Limpiar asesores al cambiar agencia
    }));
  };

  // ✅ Manejar selección de asesores
  const handleAsesorToggle = (asesorId: string) => {
    setFormData(prev => ({
      ...prev,
      asesor_doc_ids: prev.asesor_doc_ids.includes(asesorId)
        ? prev.asesor_doc_ids.filter((id: string) => id !== asesorId)
        : [...prev.asesor_doc_ids, asesorId]
    }));
  };

  // ✅ Validar formulario
  const validarFormulario = (): string | null => {
    if (!formData.titulo.trim()) return 'El título es requerido';
    if (!formData.precio_actual_usd) return 'El precio en USD es requerido';
    if (!formData.tipo) return 'El tipo de propiedad es requerido';
    if (!formData.estado) return 'El estado (Venta/Renta) es requerido';
    if (!formData.inmobiliaria_doc_id) return 'Debes seleccionar una agencia';
    if (formData.asesor_doc_ids.length === 0) return 'Debes seleccionar al menos un asesor';

    return null;
  };

  // ✅ Actualizar relaciones bidireccionales
  const actualizarRelaciones = async (propiedadId: string) => {
    const agenciaAntigua = propiedadOriginal?.inmobiliaria_id;
    const agenciaNueva = formData.inmobiliaria_doc_id;

    const asesoresAntiguos = Array.isArray(propiedadOriginal?.asesor_ids)
      ? propiedadOriginal.asesor_ids
      : Array.isArray(propiedadOriginal?.asesor_id)
        ? propiedadOriginal.asesor_id
        : propiedadOriginal?.asesor_id
          ? [propiedadOriginal.asesor_id]
          : [];

    const asesoresNuevos = formData.asesor_doc_ids;

    // ✅ 1. ACTUALIZAR AGENCIA
    // Si cambió la agencia
    if (agenciaAntigua !== agenciaNueva) {
      // Remover de agencia antigua
      if (agenciaAntigua) {
        const qAntiguaArray = Array.isArray(agenciaAntigua) ? agenciaAntigua : [agenciaAntigua];
        for (const oldAgenciaId of qAntiguaArray) {
          const qAntigua = query(
            collection(db, 'inmobiliaria'),
            where('inmobiliaria_id', '==', oldAgenciaId)
          );
          const snapAntigua = await getDocs(qAntigua);
          for (const docSnap of snapAntigua.docs) {
            await updateDoc(doc(db, 'inmobiliaria', docSnap.id), {
              propiedad_ids: arrayRemove(propiedadId)
            });
          }
        }
      }

      // Agregar a agencia nueva
      if (agenciaNueva) {
        const qNueva = query(
          collection(db, 'inmobiliaria'),
          where('inmobiliaria_id', '==', agenciaNueva)
        );
        const snapNueva = await getDocs(qNueva);
        for (const docSnap of snapNueva.docs) {
          await updateDoc(doc(db, 'inmobiliaria', docSnap.id), {
            propiedad_ids: arrayUnion(propiedadId)
          });
        }
      }
    }

    // ✅ 2. ACTUALIZAR ASESORES
    const asesoresRemovidos = asesoresAntiguos.filter(
      (id: string) => !asesoresNuevos.includes(id)
    );
    const asesoresAgregados = asesoresNuevos.filter(
      id => !asesoresAntiguos.includes(id)
    );

    // Remover de asesores antiguos (afiliados)
    for (const asesorId of asesoresRemovidos) {
      const qAsesor = query(
        collection(db, 'asesor'),
        where('asesor_id', '==', asesorId)
      );
      const snapAsesor = await getDocs(qAsesor);
      for (const docSnap of snapAsesor.docs) {
        await updateDoc(doc(db, 'asesor', docSnap.id), {
          propiedad_ids: arrayRemove(propiedadId)
        });
      }

      // También intentar en asociados
      const qAsociado = query(
        collection(db, 'asociado'),
        where('asociado_id', '==', asesorId)
      );
      const snapAsociado = await getDocs(qAsociado);
      for (const docSnap of snapAsociado.docs) {
        await updateDoc(doc(db, 'asociado', docSnap.id), {
          propiedad_ids: arrayRemove(propiedadId)
        });
      }
    }

    // Agregar a nuevos asesores
    for (const asesorId of asesoresAgregados) {
      const qAsesor = query(
        collection(db, 'asesor'),
        where('asesor_id', '==', asesorId)
      );
      const snapAsesor = await getDocs(qAsesor);
      for (const docSnap of snapAsesor.docs) {
        await updateDoc(doc(db, 'asesor', docSnap.id), {
          propiedad_ids: arrayUnion(propiedadId)
        });
      }

      // También intentar en asociados
      const qAsociado = query(
        collection(db, 'asociado'),
        where('asociado_id', '==', asesorId)
      );
      const snapAsociado = await getDocs(qAsociado);
      for (const docSnap of snapAsociado.docs) {
        await updateDoc(doc(db, 'asociado', docSnap.id), {
          propiedad_ids: arrayUnion(propiedadId)
        });
      }
    }

    // ✅ 3. ACTUALIZAR PROYECTO (si cambió)
    const proyectoAntiguo = propiedadOriginal?.proyecto_id;
    const proyectoNuevo = formData.proyecto_id;

    if (proyectoAntiguo !== proyectoNuevo) {
      // Remover de proyecto antiguo
      if (proyectoAntiguo) {
        const qProyectoAntiguo = query(
          collection(db, 'proyectos'),
          where('proyecto_id', '==', proyectoAntiguo)
        );
        const snapProyectoAntiguo = await getDocs(qProyectoAntiguo);
        for (const docSnap of snapProyectoAntiguo.docs) {
          await updateDoc(doc(db, 'proyectos', docSnap.id), {
            propiedad_ids: arrayRemove(propiedadId)
          });
        }
      }

      // Agregar a proyecto nuevo
      if (proyectoNuevo) {
        const qProyectoNuevo = query(
          collection(db, 'proyectos'),
          where('proyecto_id', '==', proyectoNuevo)
        );
        const snapProyectoNuevo = await getDocs(qProyectoNuevo);
        for (const docSnap of snapProyectoNuevo.docs) {
          await updateDoc(doc(db, 'proyectos', docSnap.id), {
            propiedad_ids: arrayUnion(propiedadId)
          });
        }
      }
    }
  };

  // ✅ Guardar cambios
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validar
    const error = validarFormulario();
    if (error) {
      Swal.fire({
        icon: 'warning',
        title: 'Campos requeridos',
        text: error,
        confirmButtonColor: '#6c757d'
      });
      return;
    }

    // Confirmar cambios
    const result = await Swal.fire({
      icon: 'question',
      title: '¿Guardar cambios?',
      text: 'Se actualizará la propiedad y sus relaciones.',
      showCancelButton: true,
      confirmButtonText: 'Sí, guardar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33'
    });

    if (!result.isConfirmed) return;

    try {
      const propiedadData = {
        // IDs
        propiedad_id: formData.propiedad_id,
        list_number: formData.list_number.trim(),

        // Básico
        titulo: formData.titulo.trim(),
        descripcion: formData.descripcion.trim(),
        tipo: formData.tipo,
        estado: formData.estado,

        // Ubicación
        direccion: formData.direccion,

        // Especificaciones
        recamaras: parseInt(formData.recamaras) || 0,
        banos_completos: parseInt(formData.banos_completos) || 0,
        medios_banos: parseInt(formData.medios_banos) || 0,
        estacionamiento: parseInt(formData.estacionamiento) || 0,
        m2_total: parseFloat(formData.m2_total) || 0,
        m2_lote: parseFloat(formData.m2_lote) || 0,
        anos_construccion: parseInt(formData.anos_construccion) || 0,
        pisos: parseInt(formData.pisos) || 0,

        // Precios
        precio_actual_usd: parseFloat(formData.precio_actual_usd) || 0,
        precio_actual_mxn: parseFloat(formData.precio_actual_mxn) || 0,

        // Características
        amenidades: formData.amenidades,
        amueblado: formData.amueblado,
        mascotas: formData.mascotas,

        // Multimedia
        fotos: formData.fotos,
        video_url: formData.video_url.trim(),
        tour_virtual_url: formData.tour_virtual_url.trim(),

        // Relaciones
        inmobiliaria_doc_id: formData.inmobiliaria_doc_id, // STRING
        asesor_doc_ids: formData.asesor_doc_ids, // ARRAY
        proyecto_id: formData.proyecto_id || null,

        // Estado
        activo: formData.activo,
        aprobado: formData.aprobado,
        destacada: formData.destacada,
        estatus_publicacion: formData.estatus_publicacion,

        // Timestamp
        fecha_actualizacion: serverTimestamp()
      };

      // Actualizar propiedad
      await updateDoc(doc(db, 'propiedades', id!), propiedadData);

      // Actualizar relaciones bidireccionales
      await actualizarRelaciones(formData.propiedad_id);

      Swal.fire({
        icon: 'success',
        title: '¡Actualizado!',
        text: 'La propiedad fue actualizada correctamente.',
        confirmButtonColor: '#3085d6'
      }).then(() => {
        navigate('/admin-property');
      });

    } catch (error) {
      console.error('Error actualizando propiedad:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo actualizar la propiedad. Intenta nuevamente.',
        confirmButtonColor: '#d33'
      });
    }
  };

  if (!propiedadOriginal) {
    return (
      <>
        <UserNav />
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-3">Cargando propiedad...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <UserNav />

      <div className="page-title">
        <div className="container">
          <div className="row">
            <div className="col-lg-12 col-md-12">
              <h2 className="ipt-title">Editar Propiedad</h2>
              <span className="ipn-subtitle">Actualiza la información de la propiedad y gestiona sus relaciones.</span>
            </div>
          </div>
        </div>
      </div>

      <section className="py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-12 col-lg-12 col-md-12">
              <div className="card border-0 rounded-4 p-4 shadow-sm">
                <form onSubmit={handleSubmit} className="taller-form-modern">
          {/* Información Básica */}
          <div className="card mb-4">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">Información Básica</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Título</label>
                  <input
                    type="text"
                    className="form-control"
                    name="titulo"
                    value={formData.titulo}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">ID FlexMLS</label>
                  <input
                    type="text"
                    className="form-control"
                    name="list_number"
                    value={formData.list_number}
                    onChange={handleChange}
                    placeholder="Ej: 123456"
                  />
                </div>

                <div className="col-md-12 mb-3">
                  <label className="form-label">Descripción</label>
                  <textarea
                    className="form-control"
                    name="descripcion"
                    value={formData.descripcion}
                    onChange={handleChange}
                    rows={4}
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Tipo</label>
                  <select
                    className="form-select"
                    name="tipo"
                    value={formData.tipo}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Selecciona</option>
                    <option value="casa">Casa</option>
                    <option value="departamento">Departamento</option>
                    <option value="terreno">Terreno</option>
                    <option value="local">Local</option>
                    <option value="bodega">Bodega</option>
                    <option value="oficina">Oficina</option>
                    <option value="edificio">Edificio</option>
                  </select>
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Estado</label>
                  <select
                    className="form-select"
                    name="estado"
                    value={formData.estado}
                    onChange={handleChange}
                    required
                  >
                    <option value="Venta">Venta</option>
                    <option value="Renta">Renta</option>
                  </select>
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Estatus</label>
                  <select
                    className="form-select"
                    name="estatus_publicacion"
                    value={formData.estatus_publicacion}
                    onChange={handleChange}
                  >
                    <option value="pendiente">Pendiente</option>
                    <option value="disponible">Disponible</option>
                    <option value="vendida">Vendida</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Ubicación */}
          <div className="card mb-4">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">Ubicación</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Calle</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.direccion.calle}
                    onChange={(e) => handleDireccionChange('calle', e.target.value)}
                  />
                </div>

                <div className="col-md-3 mb-3">
                  <label className="form-label">Número</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.direccion.numero}
                    onChange={(e) => handleDireccionChange('numero', e.target.value)}
                  />
                </div>

                <div className="col-md-3 mb-3">
                  <label className="form-label">Unidad/Depto</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.direccion.unidad}
                    onChange={(e) => handleDireccionChange('unidad', e.target.value)}
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Área</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.direccion.area}
                    onChange={(e) => handleDireccionChange('area', e.target.value)}
                    placeholder="Ej: Bucerías, Punta Mita"
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Zona</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.direccion.zona}
                    onChange={(e) => handleDireccionChange('zona', e.target.value)}
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Código Postal</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.direccion.codigo_postal}
                    onChange={(e) => handleDireccionChange('codigo_postal', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Especificaciones */}
          <div className="card mb-4">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">Especificaciones</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3 mb-3">
                  <label className="form-label">Recámaras</label>
                  <input
                    type="number"
                    className="form-control"
                    name="recamaras"
                    value={formData.recamaras}
                    onChange={handleChange}
                    min="0"
                  />
                </div>

                <div className="col-md-3 mb-3">
                  <label className="form-label">Baños Completos</label>
                  <input
                    type="number"
                    className="form-control"
                    name="banos_completos"
                    value={formData.banos_completos}
                    onChange={handleChange}
                    min="0"
                  />
                </div>

                <div className="col-md-3 mb-3">
                  <label className="form-label">Medios Baños</label>
                  <input
                    type="number"
                    className="form-control"
                    name="medios_banos"
                    value={formData.medios_banos}
                    onChange={handleChange}
                    min="0"
                  />
                </div>

                <div className="col-md-3 mb-3">
                  <label className="form-label">Estacionamiento</label>
                  <input
                    type="number"
                    className="form-control"
                    name="estacionamiento"
                    value={formData.estacionamiento}
                    onChange={handleChange}
                    min="0"
                  />
                </div>

                <div className="col-md-3 mb-3">
                  <label className="form-label">M² Total</label>
                  <input
                    type="number"
                    className="form-control"
                    name="m2_total"
                    value={formData.m2_total}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                  />
                </div>

                <div className="col-md-3 mb-3">
                  <label className="form-label">M² Lote</label>
                  <input
                    type="number"
                    className="form-control"
                    name="m2_lote"
                    value={formData.m2_lote}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                  />
                </div>

                <div className="col-md-3 mb-3">
                  <label className="form-label">Años Construcción</label>
                  <input
                    type="number"
                    className="form-control"
                    name="anos_construccion"
                    value={formData.anos_construccion}
                    onChange={handleChange}
                    min="0"
                  />
                </div>

                <div className="col-md-3 mb-3">
                  <label className="form-label">Pisos</label>
                  <input
                    type="number"
                    className="form-control"
                    name="pisos"
                    value={formData.pisos}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Precios */}
          <div className="card mb-4">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">Precios</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Precio USD</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text">$</span>
                    </div>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.precio_actual_usd}
                      onChange={(e) => handlePrecioUSDChange(e.target.value)}
                      step="0.01"
                      min="0"
                      required
                      placeholder="250000.00"
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">USD</span>
                    </div>
                  </div>
                  <small className="form-text text-muted">
                    Precio en dólares americanos
                  </small>
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Precio MXN (calculado automáticamente)</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text">$</span>
                    </div>
                    <input
                      type="number"
                      className="form-control"
                      name="precio_actual_mxn"
                      value={formData.precio_actual_mxn}
                      onChange={handleChange}
                      step="0.01"
                      min="0"
                      placeholder="4250000.00"
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">MXN</span>
                    </div>
                  </div>
                  <small className="form-text text-muted">
                    Tasa de cambio: 1 USD = {tasaCambio} MXN (ajustable)
                  </small>
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Tasa de cambio</label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      value={tasaCambio}
                      onChange={(e) => setTasaCambio(parseFloat(e.target.value) || 20)}
                      step="0.01"
                      min="0"
                      placeholder="20.00"
                    />
                    <div className="input-group-append">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => handlePrecioUSDChange(formData.precio_actual_usd)}
                      >
                        <i className="fa fa-calculator me-1"></i>
                        Recalcular
                      </button>
                    </div>
                  </div>
                  <small className="form-text text-muted">
                    Actualiza la tasa y haz clic en recalcular
                  </small>
                </div>
              </div>
            </div>
          </div>

          {/* Agencia y Asesores */}
          <div className="card mb-4">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">Agencia y Asesores</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Agencia</label>
                  <select
                    className="form-select"
                    value={formData.inmobiliaria_doc_id}
                    onChange={(e) => handleAgenciaChange(e.target.value)}
                    required
                  >
                    <option value="">Selecciona una agencia</option>
                    {inmobiliarias.map((inmob) => (
                      <option key={inmob.id} value={inmob.id}>
                        {inmob.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Asesores (mínimo 1)</label>
                  <div
                    className="border rounded p-3"
                    style={{
                      minHeight: '150px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      backgroundColor: !formData.inmobiliaria_doc_id ? '#f8f9fa' : 'white'
                    }}
                  >
                    {!formData.inmobiliaria_doc_id ? (
                      <small className="text-muted">Primero selecciona una agencia</small>
                    ) : asesoresDisponibles.length === 0 ? (
                      <small className="text-muted">No hay asesores disponibles en esta agencia</small>
                    ) : (
                      asesoresDisponibles.map((asesor) => (
                        <div key={asesor.id} className="form-check mb-2">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`asesor-${asesor.id}`}
                            checked={formData.asesor_doc_ids.includes(asesor.id)}
                            onChange={() => handleAsesorToggle(asesor.id)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`asesor-${asesor.id}`}
                            style={{ cursor: 'pointer' }}
                          >
                            {asesor.nombre}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                  <small className="form-text text-muted">
                    Seleccionados: {formData.asesor_doc_ids.length}
                  </small>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Proyecto (opcional)</label>
                  <select
                    className="form-select"
                    name="proyecto_id"
                    value={formData.proyecto_id}
                    onChange={handleChange}
                  >
                    <option value="">Sin proyecto</option>
                    {proyectos.map((p) => (
                      <option key={p.proyecto_id} value={p.proyecto_id}>
                        {p.nombre}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Características */}
          <div className="card mb-4">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">Características</h5>
            </div>
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-md-6">
                  <div className="form-check form-switch">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="amueblado"
                      name="amueblado"
                      checked={formData.amueblado}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="amueblado">
                      Amueblado
                    </label>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-check form-switch">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="mascotas"
                      name="mascotas"
                      checked={formData.mascotas}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="mascotas">
                      Permite mascotas
                    </label>
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Amenidades</label>
                <div className="row">
                  {['Parque', 'Balcón', 'Área verde', 'Wi-Fi', 'Estacionamiento', 'Jacuzzi', 'Alberca', 'Gimnasio', 'Seguridad 24/7', 'Elevador'].map(amenidad => (
                    <div key={amenidad} className="col-md-4 col-lg-3">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`amenidad-${amenidad}`}
                          checked={formData.amenidades.includes(amenidad)}
                          onChange={() => toggleAmenidad(amenidad)}
                        />
                        <label className="form-check-label" htmlFor={`amenidad-${amenidad}`}>
                          {amenidad}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Multimedia */}
          <div className="card mb-4">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">Multimedia</h5>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <label className="form-label">Fotos</label>
                <PhotoUpload
                  onUpload={handlePhotosUpload}
                  onDelete={handlePhotoDelete}
                  existingPhotos={formData.fotos}
                  maxFiles={20}
                  maxSizeMB={10}
                  storageFolder="propiedades"
                  entityId={formData.propiedad_id || `edit-${id}`}
                />
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">URL Video</label>
                  <input
                    type="url"
                    className="form-control"
                    name="video_url"
                    value={formData.video_url}
                    onChange={handleChange}
                    placeholder="https://youtube.com/..."
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">URL Tour Virtual</label>
                  <input
                    type="url"
                    className="form-control"
                    name="tour_virtual_url"
                    value={formData.tour_virtual_url}
                    onChange={handleChange}
                    placeholder="https://..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Estado de Publicación */}
          <div className="card mb-4">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">Estado</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <div className="form-check form-switch">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="activo"
                      name="activo"
                      checked={formData.activo}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="activo">
                      Activa
                    </label>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="form-check form-switch">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="aprobado"
                      name="aprobado"
                      checked={formData.aprobado}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="aprobado">
                      Aprobada
                    </label>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="form-check form-switch">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="destacada"
                      name="destacada"
                      checked={formData.destacada}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="destacada">
                      Destacada
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Botones */}
          <div className="text-center mt-4">
            <div className="d-flex justify-content-center gap-3">
              <button
                type="submit"
                className="btn btn-primary px-5"
              >
                <i className="fas fa-save me-2"></i>
                Guardar Cambios
              </button>
              <button
                type="button"
                className="btn btn-secondary px-5"
                onClick={() => navigate('/admin-property')}
              >
                <i className="fas fa-times me-2"></i>
                Cancelar
              </button>
            </div>
          </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
